<template>
  <div class="flex flex-col md:flex-row gap-2">
    <!-- Free Package -->
    <JoszakiCard
      v-if="freePackage"
      class="text-center relative flex-1"
      hoverable
    >
      <JoszakiPill
        v-if="freePackage.badge"
        type="default"
        class="absolute -top-3.5 left-1/2 transform -translate-x-1/2"
      >
        {{ freePackage.badge }}
      </JoszakiPill>
      <p class="text-xl font-bold">
        {{ freePackage.title }}
      </p>
      <p class="text-gray-500 text-sm">
        {{ freePackage.description }}
      </p>
      <div>
        <span class="text-3xl font-bold">{{ $n(0, "currency") }}</span>
        <span class="text-gray-500 pl-2">{{
          $t("subscriptionPage.serviceType.pricePerMonth")
        }}</span>
      </div>
      <div
        v-for="(feature, index) in freePackage.features"
        :key="index"
        class="flex flex-row gap-2 items-center py-2 text-sm"
      >
        <IconComponent icon="check" class="w-4 h-4 text-primary" />
        <span>{{ feature }}</span>
      </div>
      <JoszakiButton
        v-if="freePackage.buttonLabel"
        type="default"
        expanded
        inverted
        outline
        class="mt-4"
        @click="onFreeButtonClick"
      >
        {{ freePackage.buttonLabel }}
      </JoszakiButton>
      <JoszakiButton
        v-if="showFreeRegisterButton"
        type="info"
        expanded
        class="mt-2"
        @click="onFreeRegisterClick"
      >
        {{ $t("subscriptionPage.serviceType.freeRegisterButtonLabel") }}
      </JoszakiButton>
    </JoszakiCard>

    <!-- Premium Package -->
    <JoszakiCard
      v-if="paidPackage"
      class="text-center border-primary relative flex-1"
      hoverable
    >
      <JoszakiPill
        v-if="paidPackage.badge"
        type="primary"
        class="absolute -top-3.5 left-1/2 transform -translate-x-1/2"
      >
        {{ paidPackage.badge }}
      </JoszakiPill>
      <p class="text-xl font-bold">
        {{ paidPackage.title }}
      </p>
      <p class="text-gray-500 text-sm">
        {{ paidPackage.description }}
      </p>
      <p class="text-gray-500 text-sm pt-2">
        <span
          :class="{
            'relative after:content-[\'\'] after:absolute after:w-full after:h-[4px] after:bg-error after:left-0 after:top-1/3 after:-rotate-12':
              discountPriceApplicable,
          }"
          >{{ $n(displayPriceNet, "currency") }}</span
        >
        <span v-if="discountPriceApplicable" class="pl-2">{{
          $n(serviceType.discountPriceNet, "currency")
        }}</span>
        {{ $t("subscriptionPage.serviceType.plusVat") }}
      </p>
      <div>
        <span
          class="text-3xl font-bold relative"
          :class="{
            'after:content-[\'\'] after:absolute after:w-full after:h-[4px] after:bg-error after:left-0 after:top-[40%] after:-rotate-12':
              discountPriceApplicable,
          }"
          >{{ $n(displayPriceGross, "currency") }}</span
        >
        <span v-if="discountPriceApplicable" class="pl-2 text-3xl font-bold">{{
          $n(serviceType.discountPriceGross, "currency")
        }}</span>
        <span class="text-gray-500 pl-2">{{
          $t("subscriptionPage.serviceType.pricePerMonth")
        }}</span>
      </div>
      <div
        v-for="(feature, index) in paidPackage.features"
        :key="index"
        class="flex flex-row gap-2 items-center py-2 text-sm"
      >
        <IconComponent icon="check" class="w-4 h-4 text-primary" />
        <span>{{ feature }}</span>
      </div>
      <JoszakiButton
        :disabled="isSubscribedToService || statusStore.paymentBlocked"
        :loading="subscriptionLoading"
        type="info"
        expanded
        class="mt-4"
        icon-left="bolt"
        @click="onPremiumButtonClick"
      >
        <template v-if="isSubscribedToService">
          {{ $t("subscriptionPage.serviceType.subscribed") }}
        </template>
        <template v-else-if="subscriptionStore.hasPaidSubscription">
          {{
            paidPackage.buttonLabelWithSubscription ??
            $t("subscriptionPage.serviceType.subscribedButtonLabel")
          }}
        </template>
        <template v-else>
          {{
            paidPackage.buttonLabel ??
            $t("subscriptionPage.serviceType.subscribeButtonLabel")
          }}
        </template>
      </JoszakiButton>
    </JoszakiCard>
  </div>
</template>

<script>
import {
  computed,
  onBeforeMount,
  onServerPrefetch,
} from "@nuxtjs/composition-api";
import { useUserStore } from "~/stores/user";
import {
  needsSubscriptionData,
  useSubscriptionStore,
} from "~/stores/user/subscription";
import { useAppLayoutStatusStore } from "~/stores/user/appLayoutStatus";

export default {
  name: "ServiceTypePricingCards",
  props: {
    serviceType: {
      type: Object,
      required: true,
    },
    isSubscribedToService: {
      type: Boolean,
      default: false,
    },
    discountPriceApplicable: {
      type: Boolean,
      default: false,
    },
    showFreeRegisterButton: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const userStore = useUserStore();
    const subscriptionStore = useSubscriptionStore();
    const statusStore = useAppLayoutStatusStore();

    onServerPrefetch(() => subscriptionStore.load());
    onBeforeMount(() => {
      subscriptionStore.load();
      if (needsSubscriptionData(userStore.me)) {
        statusStore.load();
      }
    });
    const subscriptionLoading = computed(
      () =>
        needsSubscriptionData(userStore.me) &&
        !subscriptionStore.isLoaded &&
        !!subscriptionStore.pending
    );

    const freePackage = computed(() => props.serviceType?.options?.freePackage);
    const paidPackage = computed(() => props.serviceType?.options?.paidPackage);

    // For active subscribers with a profession/zone (discountPriceApplicable),
    // show an inflated "marketing" price struck through instead of the real
    // price. Configured per service type in options as
    // { net, gross }. Display-only and reverts automatically once the gate
    // is false (e.g. after the subscription is cancelled).
    const visiblePrice = computed(
      () => props.serviceType?.options?.visible_price_for_active_professionals
    );
    const displayPriceNet = computed(() =>
      props.discountPriceApplicable && visiblePrice.value?.net != null
        ? visiblePrice.value.net
        : props.serviceType.priceNet
    );
    const displayPriceGross = computed(() =>
      props.discountPriceApplicable && visiblePrice.value?.gross != null
        ? visiblePrice.value.gross
        : props.serviceType.priceGross
    );

    const onFreeButtonClick = () => {
      emit("freeClick");
    };

    const onFreeRegisterClick = () => {
      emit("freeRegisterClick");
    };

    const onPremiumButtonClick = () => {
      emit("premiumClick");
    };

    return {
      userStore,
      subscriptionStore,
      statusStore,
      subscriptionLoading,
      freePackage,
      paidPackage,
      displayPriceNet,
      displayPriceGross,
      onFreeButtonClick,
      onFreeRegisterClick,
      onPremiumButtonClick,
    };
  },
};
</script>
